import React, { useEffect, useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import { Button, Link, Paper, Stack } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { tenantMgmtActions, userActions, appActions } from "../../redux/actions";
import DataTable from "../../components/DataTable";
import EditButton from "../../components/EditButton";
import SwitchButton from "../../components/SwitchButton";
import DeleteTenant from "../../components/DeleteTenant";
import CreateTenant from "../../components/CreateTenant";
import TenantDetailsDrawer from "../../components/TenantDetailsDrawer";
import PageHeader from "../../components/common/PageHeader";
import StatusChip from "../../components/common/StatusChip";
import PageLoader from "../../components/common/PageLoader";
import ActionIconButton from "../../components/common/ActionIconButton";
import CopyButton from "../../components/CopyButton";
import { tenantmgmtService } from "../../services";
import { getApiErrorMessage } from "../../helpers";
import { normalizeTenant, normalizeTenantList } from "../../utils/normalizeTenant";

const defaultPageSize = 100;

const TenantManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tenantsState = useSelector((state) => state?.tenantManagementReducer?.tenants);
  const tenantsLoading = useSelector(
    (state) => state?.tenantManagementReducer?.tenantsLoading
  );
  const [editingTenant, setEditingTenant] = useState(null);
  const [isAddEditFormVisible, setAddEditFormVisible] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [enableLoadingId, setEnableLoadingId] = useState("");
  const [pendingEnabled, setPendingEnabled] = useState({});

  const filteredRows = normalizeTenantList(tenantsState);

  const handleOpenTenant = (row) => {
    const tenant = normalizeTenant(row);
    if (!tenant.slug && !tenant.name) return;
    dispatch(
      userActions.setActiveTenant({
        id: tenant.slug || tenant.name,
        name: tenant.name || tenant.slug,
      })
    );
    navigate("/tenant-clients");
  };

  const openDetails = async (row) => {
    const current = normalizeTenant(row);
    setSelectedTenant(current);
    setDetailsOpen(true);
    if (!current.id) return;
    try {
      const detail = await tenantmgmtService.getTenantById(current.id);
      setSelectedTenant(normalizeTenant({ ...current, ...(detail || {}) }));
    } catch (err) {
      dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
    }
  };

  const columns = [
    { field: "id", headerName: "ID" },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      renderCell: (params) => (
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => handleOpenTenant(params.row)}
          sx={{ fontWeight: 600, textAlign: "left" }}
        >
          {params.row.name || params.row.slug || "—"}
        </Link>
      ),
    },
    { field: "email", headerName: "Email", flex: 1 },
    { field: "mobile", headerName: "Mobile", flex: 1 },
    { field: "contactPerson", headerName: "Contact Person", flex: 1 },
    {
      field: "enabled",
      headerName: "Enabled",
      flex: 1,
      renderCell: (params) => {
        const enabled = isTenantEnabled(params.row);
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <StatusChip enabled={enabled} />
            <SwitchButton
              checked={enabled}
              disabled={enableLoadingId === params.row.id}
              onToggle={(newState) => handleEnableDisable(params.row.id, newState)}
              tooltipText="Enable/Disable Tenant"
            />
          </Stack>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.9,
      minWidth: 170,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <ActionIconButton
            title="View Details"
            onClick={() => openDetails(params.row)}
            icon={<VisibilityOutlinedIcon fontSize="small" />}
          />
          <CopyButton row={params.row} />
          <EditButton onEdit={handleEditTenant} item={params.row} />
          <DeleteTenant tenant={params.row} onDeleteSuccess={handleDeleteSuccess} />
        </Stack>
      ),
    },
  ];

  useEffect(() => {
    getAllTenants();
  }, []);

  const getAllTenants = () => {
    dispatch(tenantMgmtActions.getAllTenants({}));
  };

  const isTenantEnabled = (row = {}) =>
    row.id in pendingEnabled ? pendingEnabled[row.id] : Boolean(row.enabled);

  const handleEnableDisable = async (tenantId, enabled) => {
    if (!tenantId || enableLoadingId) return;
    setPendingEnabled((prev) => ({ ...prev, [tenantId]: enabled }));
    setEnableLoadingId(tenantId);
    try {
      await tenantmgmtService.updateTenantEnabled(tenantId, enabled);
      dispatch(
        appActions.apiSuccessMessage(
          enabled ? "Tenant enabled successfully." : "Tenant disabled successfully."
        )
      );
      await Promise.resolve(dispatch(tenantMgmtActions.getAllTenants({})));
      setPendingEnabled((prev) => {
        const next = { ...prev };
        delete next[tenantId];
        return next;
      });
    } catch (err) {
      setPendingEnabled((prev) => {
        const next = { ...prev };
        delete next[tenantId];
        return next;
      });
      dispatch(
        appActions.apiErrorMessage(
          getApiErrorMessage(err) || "Failed to update tenant status."
        )
      );
    } finally {
      setEnableLoadingId("");
    }
  };

  const handleDeleteSuccess = () => {
    getAllTenants();
  };

  const handleAddTenant = () => {
    setEditingTenant(null);
    setAddEditFormVisible(true);
  };

  const handleEditTenant = (tenant) => {
    setEditingTenant(normalizeTenant(tenant));
    setDetailsOpen(false);
    setAddEditFormVisible(true);
  };

  const handleSuccess = () => {
    getAllTenants();
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Fragment>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <PageHeader
            title="Tenants"
            subtitle="System tenant list. Click a tenant name to open its Clients, Roles, Permissions, and Users."
            sx={{ mb: 0 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleAddTenant}
            sx={{ mt: 0.5, whiteSpace: "nowrap" }}
          >
            Add New Tenant
          </Button>
        </Stack>
        <Paper sx={{ p: 2.5 }}>
          {tenantsLoading ? (
            <PageLoader text="Loading tenants..." />
          ) : (
            <DataTable
              rows={filteredRows}
              loading={tenantsLoading}
              columns={columns}
              pageSize={defaultPageSize}
              showPagination={false}
              getRowId={(row) => row.id || row.slug || row.name}
              columnVisibilityModel={{
                id: false,
              }}
              styling={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f8fafc",
                },
              }}
            />
          )}
        </Paper>
      </Fragment>
      <CreateTenant
        open={isAddEditFormVisible}
        onClose={() => setAddEditFormVisible(false)}
        tenantData={editingTenant}
        onSuccess={handleSuccess}
      />
      <TenantDetailsDrawer
        open={detailsOpen}
        tenant={selectedTenant}
        onClose={() => setDetailsOpen(false)}
        onEdit={() => handleEditTenant(selectedTenant)}
        onOpenTenant={() => {
          setDetailsOpen(false);
          handleOpenTenant(selectedTenant);
        }}
      />
    </Box>
  );
};

export default TenantManagement;
